<template>
    <div class="exam-component">
        <div v-if="isEnded">
            <h2>Assessment completed</h2>

            <p><em>Please wait...</em></p>
        </div>
        <div v-else>
            <div v-if="isBusy">
                <em>Loading...</em>
            </div>
            <div v-else>

                <div v-if="showStartSlide">
                    <div class="mb-4" v-html="exam.descriptionMD" v-if="exam.description && exam.description.length > 0"></div>

                    <div class="mb-4" v-if="showStartVideo">
                        <mux-video-component :type="'exam-enrolment-admin-start'" :entity="enrolment" :track="false" />
                    </div>

                    <div  v-if="exam.requirements && exam.requirements.length > 0">
                        <h2>Requirements</h2>
                        <div class="mb-4" v-html="exam.requirementsMD"></div>
                    </div>

                    <div class="mt-4 pt-4 border-top">
                        <b-form-checkbox
                            v-model="acceptTerms"
                            name="acceptTerms"
                            :value="true"
                            :unchecked-value="false"
                            class="my-3">
                            I confirm that I will personally complete this assessment without any external assistance.
                            By ticking this box, I agree that all answers provided are my own work. By participating in
                            this assessment, you acknowledge and consent to the sharing of your data with third parties.
                            This data sharing is essential for the qualification process and the ongoing development of
                            our products. Your information will be handled with care and used solely for the purpose of
                            providing you with your qualification and enhancing the quality and effectiveness of our
                            educational offerings.
                        </b-form-checkbox>

                        <div v-if="acceptTerms" class="text-center">
                            <button class="btn btn-primary mb-3" @click="showStartSlide = false; toTop()">Start Assessment</button>
                            <p class="help-text">
                                <small>
                                    As you progress through each module in the assessment your progress will be saved, and you can return to the assessment at any time.<br>
                                    You will <strong>not</strong> be able to re-take modules you have already completed.
                                </small>
                            </p>
                        </div>
                    </div>
                </div>

                <div v-else>

                    <div v-if="currentModule">

                        <div class="text-center mb-4">
                            <h5>Module {{ currentModuleIndex + 1 }} of {{ exam.modules.length }}</h5>
                        </div>

                        <module-component
                            :module="currentModule"
                            :user-modules="enrolment.userModules"
                            @complete-module="handleModuleCompletion"
                            :is-busy="isBusy"
                            :exam-timer-type="exam.exam_timer_type"
                        />
                    </div>
                    <div v-else>
                        <p class="mb-0">Exam completed or no modules available.</p>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ModuleComponent from './ModuleComponent.vue';

export default {
    components: {
        ModuleComponent
    },

    props: {
        examData: {
            type: Object,
            default: null
        },
        enrolmentData: {
            type: Object,
            default: null
        },
    },

    data() {
        return {
            exam: {}, // Exam data
            enrolment: {},
            currentModuleIndex: 0, // Index of the current module
            isBusy: false,
            isEnded: false,
            isAssessed: false,
            showStartVideo: false,
            showStartSlide: true,
            acceptTerms: false,
        };
    },

    mounted() {
        this.exam = this.examData;
        this.enrolment = this.enrolmentData;

        // Assessed exams of any kind have different text
        if(this.exam.exam_type.id === 2 || this.exam.exam_type.id === 3) {
            this.isAssessed = true;
        }

        if(!this.isAssessed) {
            // Shuffle questions for each module
            this.exam.modules.forEach(module => {
                this.shuffleArray(module.questions);
            });
        }

        // 'Assessed' exams have a pre-start and post-finish step that shows a video
        if(this.exam.exam_type.id === 2) {
            this.showStartVideo = true;
        }

        this.fetchExamProgress();
    },

    computed: {
        currentModule() {
            return this.exam.modules ? this.exam.modules[this.currentModuleIndex] : null;
        }
    },
    methods: {
        fetchExamProgress() {
            // API call to fetch exam data
            this.isBusy = true;
            axios.get('/api/examroom/' + this.enrolment.id + '/progress')
                .then((response) => {
                    this.currentModuleIndex = response.data.currentModule;

                    // If it's not the first module, don't show the start slide
                    if(this.currentModuleIndex !== 0) {
                        this.showStartSlide = false;
                    }
                    this.isBusy = false;
                });
        },

        handleModuleCompletion(answers) {
            // Logic to handle the completion of a module
            this.updateModuleProgress(answers);
        },

        updateModuleProgress(formData) {
            this.isBusy = true;

            axios({
                method: 'post',
                url: '/api/examroom/' + this.enrolment.id + '/module/' + this.currentModule.id + '/complete',
                data: formData,
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
                .then(response => {
                    // Success handling
                    this.handleProgressUpdateSuccess(response);
                })
                .catch(error => {
                    // Error handling
                    this.handleProgressUpdateError(error);
                })
                .finally(() => {
                    this.isBusy = false;
                })
        },

        handleProgressUpdateSuccess(response) {
            // Handle the successful update
            // For example, showing a success message, moving to the next module
            console.log('Module completed successfully', response);
            this.moveToNextModule();
        },

        handleProgressUpdateError(error) {
            // Handle the error case
            // For example, showing an error message to the user
            console.error('Error completing module:', error);
        },

        moveToNextModule() {
            if (this.currentModuleIndex < this.exam.modules.length - 1) {
                this.currentModuleIndex++;
                // Flush current answers
                this.answers = null;
            } else {
                this.currentModuleIndex = -1; // or any other logic to mark exam completion
                this.isEnded = true;

                // Emit an event to broadcast exam is ended. Do this in vanilla JS because the event is listened to outside of vue
                var event = new CustomEvent('exam-ended');

                // Dispatch the event
                document.dispatchEvent(event);

                // Redirect to success page
                window.location.href = this.enrolment.examroomSuccessUri;
            }

            this.toTop();
        },

        // Fisher-Yates (Knuth) Shuffle algorithm
        shuffleArray(array) {
            let currentIndex = array.length, temporaryValue, randomIndex;

            // While there remain elements to shuffle...
            while (0 !== currentIndex) {

                // Pick a remaining element...
                randomIndex = Math.floor(Math.random() * currentIndex);
                currentIndex -= 1;

                // And swap it with the current element.
                temporaryValue = array[currentIndex];
                array[currentIndex] = array[randomIndex];
                array[randomIndex] = temporaryValue;
            }
        },

        // Scroll to top
        toTop() {
            window.scrollTo(0, 0);
        },
    }
};
</script>
